import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Leaf, AlertTriangle, Clock, Pill, Sparkles } from "lucide-react";

export default function SharedView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/share/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const code = e?.response?.status;
        setErr(code === 410 ? "This share link has expired." : "Share link not found.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16 text-center">
        <p style={{ color: "var(--text-2)" }}>Loading…</p>
      </main>
    );
  }
  if (err) {
    return (
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-20 text-center" data-testid="shared-error">
        <Leaf className="w-6 h-6 mx-auto mb-3" strokeWidth={1.5} style={{ color: "var(--brand)" }} />
        <p className="font-serif text-3xl mb-2">{err}</p>
        <p className="text-sm" style={{ color: "var(--text-2)" }}>
          Please ask the owner for an updated link.
        </p>
      </main>
    );
  }

  const r = data.recommendation;
  return (
    <main className="max-w-3xl mx-auto px-6 sm:px-8 py-10 sm:py-16" data-testid="shared-view">
      <header className="mb-10">
        <p className="label-eyebrow mb-3">Practitioner view · Read-only</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight">
          {data.owner_name}&rsquo;s wellness plan
        </h1>
        <p className="mt-3 text-sm" style={{ color: "var(--text-2)" }}>
          Generated{" "}
          {new Date(r.created_at).toLocaleDateString(undefined, { dateStyle: "long" })}{" "}
          · Link expires{" "}
          {new Date(data.expires_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
        </p>
      </header>

      {r.profile && (
        <section className="card-soft p-6 mb-8" style={{ background: "var(--bg-2)" }}>
          <p className="label-eyebrow mb-3">Profile summary</p>
          <div className="grid sm:grid-cols-2 gap-2 text-sm" style={{ color: "var(--text-2)" }}>
            <div><strong style={{ color: "var(--text)" }}>Age:</strong> {r.profile.age_range}</div>
            <div><strong style={{ color: "var(--text)" }}>Gender:</strong> {r.profile.gender}</div>
            <div><strong style={{ color: "var(--text)" }}>Activity:</strong> {r.profile.activity_level}</div>
            <div><strong style={{ color: "var(--text)" }}>Diet:</strong> {r.profile.diet}</div>
            <div><strong style={{ color: "var(--text)" }}>Sleep:</strong> {r.profile.sleep_quality}</div>
            <div><strong style={{ color: "var(--text)" }}>Stress:</strong> {r.profile.stress_level}</div>
          </div>
          {r.profile.notes && (
            <p className="mt-3 text-sm" style={{ color: "var(--text-2)" }}>
              <strong style={{ color: "var(--text)" }}>Notes:</strong> {r.profile.notes}
            </p>
          )}
        </section>
      )}

      {r.ai_explanation && (
        <section className="card-soft p-7 mb-8" style={{ background: "var(--bg-2)" }}>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
            <p className="label-eyebrow" style={{ color: "var(--accent)" }}>Personalised note</p>
          </div>
          <div className="font-serif text-lg leading-relaxed whitespace-pre-line">
            {r.ai_explanation}
          </div>
        </section>
      )}

      <div className="space-y-4">
        {r.supplements.map((s) => (
          <article key={s.key} className="card-soft p-6" data-testid={`shared-supp-${s.key}`}>
            <div className="flex flex-wrap justify-between gap-2 mb-3">
              <div>
                <p className="label-eyebrow mb-1">{s.category}</p>
                <h2 className="font-serif text-2xl">{s.name}</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {s.addresses.map((a) => (
                  <span key={a} className="text-xs px-3 py-1 rounded-full"
                    style={{ background: "var(--bg-2)", color: "var(--text-2)", border: "1px solid var(--border)" }}>
                    {a}
                  </span>
                ))}
              </div>
            </div>
            <p className="mb-4" style={{ color: "var(--text-2)" }}>{s.benefits}</p>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-2"><Pill className="w-4 h-4 mt-0.5" strokeWidth={1.5} style={{ color: "var(--brand)" }} /> <span><strong>Dosage:</strong> {s.dosage}</span></div>
              <div className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5" strokeWidth={1.5} style={{ color: "var(--brand)" }} /> <span><strong>Timing:</strong> {s.timing}</span></div>
            </div>
            <div className="mt-4 p-3 rounded-xl text-sm" style={{ background: "#FBF4F0", border: "1px solid #ECDCD3" }}>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5" strokeWidth={1.5} style={{ color: "var(--warning)" }} />
                <div className="space-y-1">
                  <p><strong>Warnings:</strong> {s.warnings}</p>
                  <p><strong>Interactions:</strong> {s.interactions}</p>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>

      <footer className="mt-12 text-xs" style={{ color: "var(--text-2)" }}>
        Shared via Alternavit. Educational only — not medical advice.
      </footer>
    </main>
  );
}
